import {
  CADPoint,
  CompoundCurve,
  ICADObject,
} from '@technology/web3d-plugin-line-editing';
import { UnreachableCaseError } from 'ts-essentials';
import {
  closedRing,
  compoundCurveSceneVertices,
  featureGeometryFromCurve,
} from '../types/curve-geometry';
import {
  type FeatureGeometry,
  type GeometryKind,
  type Position3D,
} from '../types/geometry';

export interface CadVertex {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface CadGeometryReader {
  toGeometry(entity: ICADObject): FeatureGeometry;
}

export function cadGeometryKind(
  entity: CADPoint | CompoundCurve
): GeometryKind {
  if (entity instanceof CADPoint) {
    return 'Point';
  }
  return featureGeometryFromCurve(entity).type;
}

/**
 * Assembles geometry from coordinates the caller already projected, so the
 * read path and every write path share one CAD-type dispatch.
 */
export function cadGeometryFromCoordinates(
  kind: GeometryKind,
  coordinates: Position3D[]
): FeatureGeometry {
  switch (kind) {
    case 'Point':
      return { type: 'Point', coordinates: coordinates[0] };
    case 'LineString':
      return { type: 'LineString', coordinates };
    case 'Polygon':
      return { type: 'Polygon', coordinates: [closedRing(coordinates)] };
    default:
      throw new UnreachableCaseError(kind);
  }
}

/** Scene-space vertices in authoring order; a point contributes exactly one. */
export function cadVertices(
  entity: CADPoint | CompoundCurve
): readonly CadVertex[] {
  return entity instanceof CADPoint
    ? [entity.position]
    : compoundCurveSceneVertices(entity);
}

export function createCadGeometryReader(): CadGeometryReader {
  return {
    toGeometry(entity: ICADObject): FeatureGeometry {
      if (!(entity instanceof CADPoint) && !(entity instanceof CompoundCurve)) {
        throw new UnreachableCaseError(entity as never);
      }
      if (entity instanceof CompoundCurve) {
        return featureGeometryFromCurve(entity);
      }
      return cadGeometryFromCoordinates(
        cadGeometryKind(entity),
        cadVertices(entity).map(
          (vertex): Position3D => [vertex.x, vertex.y, vertex.z]
        )
      );
    },
  };
}
